import type { FlagName, Permission } from '@crm/contracts';
import {
  Briefcase,
  Building2,
  ChartColumn,
  HandCoins,
  KeyRound,
  ClipboardList,
  Flag,
  Home,
  ListChecks,
  ListTodo,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  UsersRound,
  Webhook,
  HeartPulse,
  UserSearch,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

/**
 * The sidebar, as data. Every entry names the permission AND the flag that reveal it —
 * the same two gates the API applies to the route behind it (`config: { permission,
 * feature }`), so the navigation never offers a page the API would answer 403 to.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission: Permission;
  feature: FlagName | 'always';
  /** Sidebar section. A group's entries are contiguous in `NAV`, so sections keep its order. */
  group: NavGroup;
  /** One line under the tool's name on Home. */
  description: string;
}

export type NavGroup = 'workspace' | 'admin' | 'configure';
export const NAV_GROUP_LABEL: Record<NavGroup, string> = {
  workspace: 'Workspace',
  admin: 'Admin',
  configure: 'Configure',
};

export const NAV: readonly NavItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: Home,
    permission: 'organisation.read',
    feature: 'always',
    group: 'workspace',
    description: 'Your overview.',
  },
  {
    to: '/candidates',
    label: 'Candidates',
    icon: UserSearch,
    permission: 'candidate.read',
    feature: 'candidates.enabled',
    group: 'workspace',
    description: 'Every candidate, their pipeline and history.',
  },
  {
    to: '/projects',
    label: 'Projects',
    icon: Briefcase,
    permission: 'customer.read',
    feature: 'customers.enabled',
    group: 'workspace',
    description: 'Client engagements with their contacts, deals and jobs.',
  },
  {
    to: '/companies',
    label: 'Companies',
    icon: Building2,
    permission: 'customer.read',
    feature: 'customers.enabled',
    group: 'workspace',
    description: 'The client companies behind each project.',
  },
  {
    to: '/deals',
    label: 'Deals',
    icon: HandCoins,
    permission: 'customer.read',
    feature: 'customers.enabled',
    group: 'workspace',
    description: 'Payment lines across every project, by status.',
  },
  // In the workspace, beside the records that move through them, not under Configure: people
  // open it to see and pick pipelines, not only to edit them. The URL stays /settings/pipelines.
  {
    to: '/settings/pipelines',
    label: 'Pipelines',
    icon: Workflow,
    permission: 'organisation.read',
    feature: 'always',
    group: 'workspace',
    description: 'The stages records move through.',
  },
  {
    to: '/jobs',
    label: 'Jobs',
    icon: ClipboardList,
    permission: 'job.read',
    feature: 'jobs.enabled',
    group: 'workspace',
    description: 'Open roles across all projects.',
  },
  // Every member has a task list; tasks on records they cannot see are still theirs to finish.
  {
    to: '/tasks',
    label: 'My Tasks',
    icon: ListTodo,
    permission: 'organisation.read',
    feature: 'always',
    group: 'workspace',
    description: 'What is assigned to you, by due date.',
  },
  {
    to: '/team-tasks',
    label: 'Team Tasks',
    icon: ListChecks,
    permission: 'task.read',
    feature: 'always',
    group: 'workspace',
    description: "Your team's tasks, by due date and person.",
  },
  {
    to: '/record-counts',
    label: 'Record counts',
    icon: ChartColumn,
    permission: 'organisation.read',
    feature: 'always',
    group: 'workspace',
    description: 'How many records of each kind the organisation holds.',
  },
  {
    to: '/users',
    label: 'Users',
    icon: Users,
    permission: 'user.read',
    feature: 'always',
    group: 'admin',
    description: 'Members of the organisation and their roles.',
  },
  {
    to: '/teams',
    label: 'Teams',
    icon: UsersRound,
    permission: 'team.read',
    feature: 'always',
    group: 'admin',
    description: 'Who works together, and who leads each team.',
  },
  {
    to: '/audit',
    label: 'Audit log',
    icon: ScrollText,
    permission: 'audit_log.read',
    feature: 'audit_log.enabled',
    group: 'admin',
    description: 'Who changed what, and when.',
  },
  {
    to: '/flags',
    label: 'Feature flags',
    icon: Flag,
    permission: 'flag.read',
    feature: 'always',
    group: 'admin',
    description: 'Switch features on or off for the organisation.',
  },
  {
    to: '/settings/roles',
    label: 'Roles',
    icon: ShieldCheck,
    permission: 'role.read',
    feature: 'always',
    group: 'admin',
    description: 'What each role can do, and custom roles of your own.',
  },
  {
    to: '/settings/fields',
    label: 'Fields',
    icon: SlidersHorizontal,
    permission: 'field_definition.read',
    feature: 'custom_fields.enabled',
    group: 'configure',
    description: 'Custom fields on candidates.',
  },
  {
    to: '/settings/api-keys',
    label: 'API keys',
    icon: KeyRound,
    permission: 'integration.read',
    feature: 'api_keys.enabled',
    group: 'configure',
    description: 'Keys for integrations calling the API.',
  },
  {
    to: '/settings/webhooks',
    label: 'Webhooks',
    icon: Webhook,
    permission: 'integration.read',
    feature: 'webhooks.enabled',
    group: 'configure',
    description: 'Send record changes to other systems as they happen.',
  },
  {
    to: '/settings/system-health',
    label: 'System Health',
    icon: HeartPulse,
    permission: 'organisation.manage',
    feature: 'always',
    group: 'configure',
    description: 'Breakers, queues, failed jobs, sync and log levels.',
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Settings,
    permission: 'organisation.read',
    feature: 'always',
    group: 'configure',
    description: 'Your preferences and the system status.',
  },
];

export function visibleNav(
  items: readonly NavItem[],
  gates: { can: (permission: Permission) => boolean; isEnabled: (flag: FlagName) => boolean },
): NavItem[] {
  return items.filter(
    (item) =>
      gates.can(item.permission) && (item.feature === 'always' || gates.isEnabled(item.feature)),
  );
}

/** Consecutive runs of one group, for the sidebar's sections (`NAV` keeps groups contiguous). */
export function groupNav(items: readonly NavItem[]): { group: NavGroup; items: NavItem[] }[] {
  const out: { group: NavGroup; items: NavItem[] }[] = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else out.push({ group: item.group, items: [item] });
  }
  return out;
}
